import React from 'react'
import './Account.css'
import {doLogin} from './loginActions.js'
import LoginScaffold from './LoginScaffold.js'
import {getDiscoverableSignInOptions, getSignInOptions, PasskeySignInError, signInWithPasskey, supportsPasskeys} from './passkeys.js'
import {sendPasswordResetEmail} from './resetPasswordActions.js'

type LoginStep = 'email' | 'passkey' | 'password'

class LoginForm extends React.Component<{}, {
    email: string, password: string, error: string, step: LoginStep, busy: boolean, resetBusy: boolean
}> {
    state: {email: string, password: string, error: string, step: LoginStep, busy: boolean, resetBusy: boolean} = {
        email: '', password: '', error: '', step: 'email', busy: false, resetBusy: false
    }
    private conditionalAbort?: AbortController
    private explicitAbort?: AbortController
    private emailInput = React.createRef<HTMLInputElement>()
    private passwordInput = React.createRef<HTMLInputElement>()

    componentDidMount() {
        this.emailInput.current?.focus()
        this.startConditionalPasskeySignIn()
    }

    componentDidUpdate(_: Readonly<{}>, previousState: Readonly<typeof this.state>) {
        if (previousState.step === this.state.step) return
        if (this.state.step === 'email') this.emailInput.current?.focus()
        if (this.state.step === 'password') this.passwordInput.current?.focus()
    }

    componentWillUnmount() {
        this.conditionalAbort?.abort()
        this.explicitAbort?.abort()
    }

    startConditionalPasskeySignIn() {
        if (!supportsPasskeys()) return
        const abort = new AbortController()
        this.conditionalAbort = abort
        void (async () => {
            try {
                const credentialClass = window.PublicKeyCredential as typeof PublicKeyCredential & {
                    isConditionalMediationAvailable?: () => Promise<boolean>
                }
                if (!credentialClass.isConditionalMediationAvailable ||
                    !await credentialClass.isConditionalMediationAvailable() || abort.signal.aborted) return
                const ceremony = await getDiscoverableSignInOptions()
                if (abort.signal.aborted) return
                await signInWithPasskey(ceremony, 'conditional', abort.signal)
                if (abort.signal.aborted) return
                window.location.href = '/'
            } catch (error) {
                if (!abort.signal.aborted && error instanceof PasskeySignInError) {
                    this.setState({error: `${error.message} Sign in with your password instead.`})
                }
            } finally {
                if (this.conditionalAbort === abort) this.conditionalAbort = undefined
            }
        })()
    }

    stopConditionalPasskeySignIn() {
        this.conditionalAbort?.abort()
        this.conditionalAbort = undefined
    }

    stopExplicitPasskeySignIn() {
        this.explicitAbort?.abort()
        this.explicitAbort = undefined
    }

    backToEmail() {
        this.stopExplicitPasskeySignIn()
        this.setState({step: 'email', password: '', error: '', busy: false},
            () => this.startConditionalPasskeySignIn())
    }

    async continueWithEmail(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault()
        const email = this.emailInput.current?.value.trim() || ''
        if (!email) {
            this.emailInput.current?.focus()
            this.emailInput.current?.reportValidity()
            return
        }
        this.stopConditionalPasskeySignIn()
        this.setState({email, error: '', busy: true})
        try {
            const result = await getSignInOptions(email)
            if (!result.exists) {
                sessionStorage.setItem('signupEmail', email)
                window.location.href = '/create-account'
            } else {
                if (result.hasPasskey && result.ceremonyId && result.options && supportsPasskeys()) {
                    const abort = new AbortController()
                    this.explicitAbort = abort
                    this.setState({step: 'passkey'})
                    try {
                        await signInWithPasskey({ceremonyId: result.ceremonyId, options: result.options}, undefined, abort.signal)
                        if (abort.signal.aborted) return
                        window.location.href = '/'
                    } catch (error) {
                        if (abort.signal.aborted) return
                        this.setState({step: 'password', error: error instanceof PasskeySignInError ? error.message : ''})
                    } finally {
                        if (this.explicitAbort === abort) this.explicitAbort = undefined
                    }
                } else this.setState({step: 'password'})
            }
        } catch {
            this.setState({step: 'password'})
        } finally {
            this.setState({busy: false})
        }
    }

    async passwordLogin(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault()
        this.setState({busy: true, error: ''})
        try {
            const response = await doLogin(this.state.email, this.state.password)
            if (!response.success) this.setState({error: response.message || 'Sign-in failed.'})
            else window.location.href = '/'
        } catch {
            this.setState({error: 'Sign-in failed. Please try again.'})
        } finally {
            this.setState({busy: false})
        }
    }

    async requestPasswordReset() {
        this.setState({resetBusy: true, error: ''})
        try {
            const response = await sendPasswordResetEmail(this.state.email)
            if (!response.success) throw new Error(response.message || 'Could not send the reset email.')
            sessionStorage.setItem('passwordResetSent', 'true')
            window.location.href = '/forgot-password'
        } catch (error) {
            this.setState({error: error instanceof Error ? error.message : 'Could not send the reset email.'})
        } finally {
            this.setState({resetBusy: false})
        }
    }

    render() {
        return <LoginScaffold>
            <header className="Login-header">
                {this.state.step === 'email' ? 'Enter your email to continue.' :
                    this.state.step === 'passkey' ? 'Use your passkey to sign in.' : 'Sign in to your account.'}
            </header>
            {this.state.error && <p className="Login-header-error" role="alert">{this.state.error}</p>}
            {this.state.step === 'email' && <form className="Login-form Login-email-form" onSubmit={event => this.continueWithEmail(event)}>
                <input ref={this.emailInput} type="email" name="email" autoComplete="username webauthn" required className="Login-input"
                    aria-label="Email" placeholder="Email" defaultValue={this.state.email}
                    onPaste={() => this.setState({error: ''})}
                    onChange={event => this.setState({email: event.target.value, error: ''})} />
                <button type="submit" className="primary Login-continue-button" disabled={this.state.busy}>Continue</button>
            </form>}
            {this.state.step === 'passkey' && <div className="Login-form Login-passkey-form">
                <button type="button" className="Login-back-button" onClick={() => this.backToEmail()}>← Back</button>
                <p className="Login-account-email">{this.state.email}</p>
                <button type="button" className="Login-reset-password-button Login-use-password-button" onClick={() => {
                    this.stopExplicitPasskeySignIn()
                    this.setState({step: 'password', busy: false})
                }}>Use password instead</button>
            </div>}
            {this.state.step === 'password' && <form className="Login-form Login-password-form" onSubmit={event => this.passwordLogin(event)}>
                <button type="button" className="Login-back-button" onClick={() => this.backToEmail()}>← Back</button>
                <p className="Login-account-email">{this.state.email}</p>
                <input ref={this.passwordInput} type="password" autoComplete="current-password" required className="Login-input"
                    aria-label="Password" placeholder="Password" value={this.state.password}
                    onChange={event => this.setState({password: event.target.value})} />
                <div className="Login-password-actions">
                    <button type="submit" className="primary Login-continue-button" disabled={this.state.busy || this.state.resetBusy}>Sign in</button>
                    <button type="button" className="Login-reset-password-button" disabled={this.state.busy || this.state.resetBusy}
                        onClick={() => this.requestPasswordReset()}>Reset password</button>
                </div>
            </form>}
        </LoginScaffold>
    }
}

export default LoginForm
